import { useMutation } from "@tanstack/react-query";
import { saveAs } from "file-saver";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { fetchDataGetBlobFile } from "@/core/api/api-methods";
import { logger } from "@/core/utils/func/logger";
import { payrollEndpoints } from "@/features/payroll/api/endpoints";

interface Args {
  batchId: string;
}

/**
 * Télécharge l'archive ZIP des relevés individuels (`GET
 * /PayrollBatches/{id}/statements`) : un PDF par salarié payé, nommé
 * `releve-NNNN-nom.pdf`. Codes : 200 `application/zip` ; 404 ; 422 aucun
 * salarié payé.
 */
export const useDownloadPayrollBatchStatements = () =>
  useMutation({
    mutationKey: ["payroll", "payrollBatches", "statements"],
    mutationFn: async ({ batchId }: Args) => {
      const { blob, filename } = await fetchDataGetBlobFile({
        url: payrollEndpoints.payrollBatches.statements(batchId),
        acceptHeader: "application/zip",
      });
      if (blob.type.includes("json")) {
        const text = await blob.text();
        throw new Error(text || "Payroll statements download failed");
      }
      saveAs(blob, filename ?? `releves-paie-${batchId}.zip`);
      return filename;
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useDownloadPayrollBatchStatements failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "payroll" }
      );
    },
  });
